import React, { useState, useCallback, useMemo, useRef } from 'react';
import { sanitizeValue } from '@utilities';

import { AutoComplete } from 'primereact/autocomplete';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import { PRIME_REACT_DATE_FORMAT } from '@utilities/date';
import { Chips } from 'primereact/chips';
import { RadioButton } from 'primereact/radiobutton';
import {
	isBuilderCostingPercentField,
	isBuilderCostingEditablePricingField,
	isBuilderCostingPricingField,
	updateBuilderCostingPricingFormValue
} from '@utilities/builderCostingPricing';
import { matchesCatalogSearch } from '@utilities/lightMaterialCategories';

/** Searchable dropdown: click input to open all options, type to filter. No dropdown button. */
const SearchableDropdownField = ({
	field,
	tag,
	props,
	isFormFieldInvalid,
	setFieldValue
}: Record<string, any>) => {
	const acRef = useRef<any>(null);
	const isFnLabel = typeof props.optionLabel === 'function';
	const optionValue = props.optionValue; // e.g. "key", "userId" — when set, store scalar instead of object
	const labelKey = isFnLabel
		? '__label'
		: typeof props.optionLabel === 'string'
		? props.optionLabel
		: undefined;

	// Pre-compute __label on each option when optionLabel is a function
	const enrichedOptions = useMemo(() => {
		const options = props.options || [];
		if (!isFnLabel) return options;
		return options.map((item: Record<string, any>) => ({ ...item, __label: props.optionLabel(item) }));
	}, [props.options, props.optionLabel, isFnLabel]);

	const [filteredOptions, setFilteredOptions] = useState<Array<Record<string, any>>>([]);
	const [queryText, setQueryText] = useState<string | null>(null);

	// When optionValue is set, field.value is a scalar (e.g. an ID). Resolve it to the full object for display.
	// When optionValue is not set, field.value is the full object.
	const displayValue = useMemo(() => {
		const val = field.value;
		if (optionValue && val != null && typeof val !== 'object') {
			// Scalar stored — find the matching option
			const match = enrichedOptions.find((opt: Record<string, any>) => opt[optionValue] === val);
			return match || val;
		}
		if (!val || typeof val !== 'object' || !isFnLabel) return val;
		if (!val.__label) return { ...val, __label: props.optionLabel(val) };
		return val;
	}, [field.value, props.optionLabel, isFnLabel, optionValue, enrichedOptions]);

	const searchMethod = useCallback(
		(event: any) => {
			const query = (event.query || '').toLowerCase().trim();
			if (!query) {
				// Always create a new array so React detects the change and shows the panel
				setFilteredOptions([...enrichedOptions]);
				return;
			}
			setFilteredOptions(
					enrichedOptions.filter((item: Record<string, any>) =>
						matchesCatalogSearch(item, query, typeof props.filterBy === 'string' ? props.filterBy.split(',').map((key: string) => key.trim()) : [labelKey || 'name'])
					)
			);
		},
		[enrichedOptions, labelKey, props.filterBy]
	);

	// On focus, show all options immediately by triggering search
	const handleFocus = useCallback(
		(e: any) => {
			// Small delay to ensure the component is ready to show panel
			setTimeout(() => {
				if (acRef.current) {
					acRef.current.search(e, '', 'dropdown');
				}
			}, 50);
		},
		[]
	);

	// Close panel and blur input after selection
	const handleSelect = useCallback((event: any) => {
		const selected = event?.value;
		const selectedValue = optionValue && selected && typeof selected === 'object' ? selected[optionValue] : selected;
		setQueryText(null);
		if (props.onChange) props.onChange({ ...event, value: selectedValue });
		else setFieldValue(tag, selectedValue);
		setTimeout(() => {
			if (acRef.current) {
				acRef.current.hide();
				// Blur the input so re-clicking opens fresh
				const inputEl = acRef.current.getInput?.() || acRef.current.inputRef?.current;
				if (inputEl) inputEl.blur();
			}
		}, 0);
	}, [optionValue, props.onChange, setFieldValue, tag]);

	return (
		<AutoComplete
			ref={acRef}
			forceSelection
			minLength={0}
			delay={0}
			suggestions={filteredOptions}
			completeMethod={searchMethod}
			field={labelKey}
			className={classNames({ 'w-full': true, 'p-invalid': isFormFieldInvalid(tag) })}
			value={queryText !== null ? queryText : displayValue}
			onFocus={handleFocus}
			onSelect={handleSelect}
			onChange={e => {
				if (typeof e.value === 'string') setQueryText(e.value);
			}}
			itemTemplate={props.itemTemplate}
			pt={{
				root: { className: 'w-full' },
				input: { className: 'w-full' },
				...props.pt
			}}
			disabled={props.disabled}
			placeholder={props.placeholder}
		/>
	);
};

export const InputControlField = ({
	type,
	field,
	tag,
	props,
	builderCostingPricing,
	builderCostingGrossPriceField,
	inplace,
	isFormFieldInvalid,
	setFieldValue,
	values,
	inputRef,
	autocompleteSuggestions
}: Record<string, any>) => {
	const { wrapperClassName: _wrapperClassName, ...fieldProps } = props || {};
	const handleBlur = e => {
		const sanitizedValue = sanitizeValue(e.target.value);
		setFieldValue(tag, sanitizedValue);
		field.onBlur(e);
	};
	if (type === 'number') {
		const grossPriceField = builderCostingGrossPriceField || 'basePrice';
		const isBuilderCostingPricingNumber =
			builderCostingPricing && isBuilderCostingEditablePricingField(tag, grossPriceField);
		const isPercentField = isBuilderCostingPricingNumber && isBuilderCostingPricingField(tag) && isBuilderCostingPercentField(tag);

		return (
			<InputNumber
				autoFocus={inplace}
				useGrouping={false}
				className={classNames({
					'w-full': true,
					'p-invalid': isFormFieldInvalid(tag)
				})}
				{...field}
				pt={{
					input: {
						root: { className: 'w-full' }
					}
				}}
				{...fieldProps}
				min={isBuilderCostingPricingNumber ? 0 : fieldProps.min}
				max={isPercentField ? 100 : fieldProps.max}
				onChange={e => {
					if (isBuilderCostingPricingNumber) {
						updateBuilderCostingPricingFormValue(values, setFieldValue, tag, e.value, grossPriceField);
					} else {
						setFieldValue(tag, e.value);
					}
				}}
			/>
		);
	} else if (type === 'email' || type === 'text') {
		return (
			<InputText
				autoFocus={inplace}
				type={type}
				className={classNames({
					'w-full': true,
					'p-invalid': isFormFieldInvalid(tag)
				})}
				{...field}
				{...fieldProps}
			/>
		);
	} else if (type === 'textarea') {
		return (
			<InputTextarea
				autoFocus={inplace}
				className={classNames({
					'w-full': true,
					'p-invalid': isFormFieldInvalid(tag)
				})}
				{...field}
				{...fieldProps}
				onBlur={handleBlur}
			/>
		);
	} else if (type === 'dropdown') {
		return (
			<Dropdown
				autoFocus={inplace}
				className={classNames({
					'w-full': true,
					'p-invalid': isFormFieldInvalid(tag)
				})}
				{...field}
				{...fieldProps}
			/>
		);
	} else if (type === 'searchable-dropdown') {
		return (
			<SearchableDropdownField
				field={field}
				tag={tag}
				props={fieldProps}
				isFormFieldInvalid={isFormFieldInvalid}
				setFieldValue={setFieldValue}
			/>
		);
	} else if (type === 'multiselect') {
		return (
			<MultiSelect
				autoFocus={inplace}
				className={classNames({
					'w-full': true,
					'p-invalid': isFormFieldInvalid(tag)
				})}
				optionLabel="label"
				emptyFilterMessage="No options available"
				{...field}
				{...fieldProps}
			/>
		);
	} else if (type === 'calendar') {
		return (
			<Calendar
				autoFocus={inplace}
				readOnlyInput
				className="w-full"
				dateFormat={PRIME_REACT_DATE_FORMAT}
				pt={{
					panel: { className: '!w-auto !min-w-0' },
					day: { className: 'p-0' }
				}}
				{...field}
				{...fieldProps}
				onBlur={() => {}}
			/>
		);
	} else if (type === 'autocomplete') {
		return (
			<AutoComplete
				autoFocus={inplace}
				ref={inputRef}
				forceSelection
				minLength={!!fieldProps.minLength ? fieldProps.minLength : 3}
				suggestions={autocompleteSuggestions}
				selectionLimit={!!fieldProps.multiple ? fieldProps.selectionLimit || null : 1}
				showEmptyMessage={true}
				{...field}
				value={!!fieldProps.multiple ? field.value : !!field.value ? [field.value] : null}
				onChange={e => {
					setFieldValue(tag, !!fieldProps.multiple ? e.value : e.value[0] || null);
				}}
				onUnselect={() => {
					inputRef.current?.focus();
				}}
				pt={{
					...fieldProps.pt,
					root: { className: `w-full overflow-auto ${fieldProps.pt?.root?.className}` },
					container: {
						className: `w-full flex-nowrap overflow-auto ${fieldProps.pt?.container?.className}`
					},
					inputToken: {
						className: `${fieldProps.multiple ? 'shrink-0' : ''}  ${fieldProps.pt?.inputToken?.className}`
					}
				}}
				// To skip props which need not be passed down as in can be removed
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				{...(({ autoCompleteSuggestionsResponse, ...o }) => o)(fieldProps)}
				multiple={true}
			/>
		);
	} else if (type === 'chips') {
		return (
			<Chips
				{...field}
				{...fieldProps}
				className={`w-full rounded  border ${isFormFieldInvalid(tag) ? 'p-invalid' : ''}`}
				value={field.value || []}
				onChange={e => setFieldValue(tag, e.value)}
				separator=","
				pt={{
					container: {
						className: 'w-full p-1'
					},
					token: {
						className: 'my-1'
					}
				}}
			/>
		);
	} else if (type === 'radio') {
		return fieldProps.options ? (
			<div className="flex flex-wrap gap-3">
				{fieldProps.options.map((option: Record<string, any>) => (
					<div key={option.value} className="flex items-center">
						<RadioButton
							inputId={option.value.toString()}
							name={tag}
							value={option.value}
							onChange={e => setFieldValue(tag, e.value)}
							checked={field.value === option.value}
							disabled={fieldProps.disabled}
						/>
						<label htmlFor={option.value.toString()} className="ml-2">
							{option.label}
						</label>
					</div>
				))}
			</div>
		) : (
			<RadioButton
				{...field}
				{...fieldProps}
				className={classNames({
					'p-invalid': isFormFieldInvalid(tag)
				})}
				onChange={e => setFieldValue(tag, e.value)}
				checked={field.value === fieldProps.value}
			/>
		);
	} else {
		return <></>;
	}
};
